import React from 'react';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';

interface StepperProps {
    currentStep: number; // 1-based index (1, 2, 3, 4)
    steps: string[];
}

export const Stepper: React.FC<StepperProps> = ({ currentStep, steps }) => {
    // BUG 1 FIX: Use CSS Grid for precise progress line positioning
    const containerStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: `repeat(${steps.length}, 1fr)`,
        alignItems: 'center',
        marginBottom: spacing[48],
        position: 'relative',
        padding: `0 ${spacing[8]}`,
        width: '100%',
        boxSizing: 'border-box',
    };

    return (
        <div style={containerStyle}>
            {steps.map((stepLabel, index) => {
                const stepNum = index + 1;
                const isActive = stepNum === currentStep;
                const isCompleted = stepNum < currentStep;
                const isLast = index === steps.length - 1;

                // Visual Refinement: Larger touch target / presence (24px -> 32px)
                const circleSize = '32px';

                // Brass accent for active step, Navy for completed
                const bgColor = isActive ? '#C9B37E' : (isCompleted ? colors.primary.navy : '#FFFFFF');
                const borderColor = isActive ? '#C9B37E' : (isCompleted ? colors.primary.navy : '#E5E7EB');

                const circleStyle: React.CSSProperties = {
                    width: circleSize,
                    height: circleSize,
                    borderRadius: '50%',
                    backgroundColor: bgColor,
                    border: `2px solid ${borderColor}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: (isActive || isCompleted) ? '#FFFFFF' : colors.text.muted,
                    fontWeight: typography.weights.bold,
                    fontSize: '14px', // Slightly larger
                    zIndex: 2,
                    transition: 'background-color 200ms ease-out, border-color 200ms ease-out, box-shadow 200ms ease-out',
                    position: 'relative',
                    // Active state ring with Brass glow
                    boxShadow: isActive ? `0 0 0 4px rgba(201, 179, 126, 0.15)` : 'none',
                };

                const labelStyle: React.CSSProperties = {
                    position: 'absolute',
                    top: '40px', // Adjusted for larger circle
                    width: '120px',
                    textAlign: 'center',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontFamily: typography.fontFamily,
                    fontSize: typography.sizes.small.fontSize,
                    // Refinement: Active label is bolder and Navy
                    fontWeight: isActive ? typography.weights.semiBold : typography.weights.regular,
                    color: isActive ? colors.primary.navy : isCompleted ? colors.text.primary : colors.text.muted,
                    transition: 'color 150ms ease-out',
                };

                // Wrapper to center content in grid cell
                const stepWrapperStyle: React.CSSProperties = {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifySelf: 'center',
                    position: 'relative',
                    zIndex: 2,
                };

                // Line Segment Logic (Progress Bar)
                // Refinement: Render lines between steps.
                // If the next step is active or completed, the line leading to it is Navy.
                const lineStyle: React.CSSProperties = {
                    display: isLast ? 'none' : 'block', // No line after last step
                    position: 'absolute',
                    top: '16px', // Center of 32px circle
                    left: '50%', // Start from center of current circle
                    width: '100vw', // We need to span to the next container. Use flex grow on a spacer instead?
                    // Actually, since we are using space-between, absolute positioning geometry is hard to guess perfectly without flex-grow.
                    // BETTER APPROACH: Use a connector line div placed *between* circles in the markup if we weren't using space-between.
                    // BUT prompt says "Do NOT change layout structure" (too much).
                    // Stick to absolute line, but maybe use width: calc(100% - ...) is hard.
                    // Fallback: Using the previous single-line approach but with a gradient or background-fill hack?
                    // Or simply render a background line behind everything, and a "progress" line on top.
                };

                // Let's use the single background line approach but refined. 
                // We will render the line OUTSIDE the map loop for simpler structure compliance, 
                // but use a linear-gradient to show progress!

                return (
                    <div key={index} style={stepWrapperStyle}>
                        <div style={circleStyle}>
                            {isCompleted ? (
                                <svg width="14" height="14" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            ) : (
                                stepNum
                            )}
                        </div>
                        <div style={labelStyle}>
                            {stepLabel}
                        </div>
                    </div>
                );
            })}

            {/* Background Track - BUG 1 FIX: Use calc with grid fractions */}
            <div style={{
                position: 'absolute',
                top: '16px', // Center of 32px circle
                left: `calc(100% / ${steps.length} / 2)`, // Start at center of first column
                right: `calc(100% / ${steps.length} / 2)`, // End at center of last column
                height: '2px',
                backgroundColor: '#E5E7EB',
                zIndex: 1,
            }} />

            {/* Progress Fill Track - BUG 1 FIX: Width based on grid column centers */}
            <div style={{
                position: 'absolute',
                top: '16px',
                left: `calc(100% / ${steps.length} / 2)`,
                // Width spans from column 1 center to current column center
                width: currentStep === 1 ? '0' : `calc((100% - 100% / ${steps.length}) * ${(currentStep - 1) / (steps.length - 1)})`,
                height: '2px',
                backgroundColor: '#C9B37E',
                zIndex: 1,
                transition: 'width 200ms ease-out',
            }} />
        </div>
    );
};
